import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Link } from "react-router-dom";
import CardCanvas from './CardCanvas';
import { CLASSES, RARITIES, EMBLEMS, MAIN_TYPES, SUB_TYPES } from '../constants';
import { 
  FaFileImport, 
  FaFileExport, 
  FaCamera, 
  FaDownload, 
  FaMagnifyingGlass, 
  FaSliders,
  FaCircleQuestion
} from "react-icons/fa6";
import { RiImportFill } from "react-icons/ri";
import { useUser } from '../../../UserContext';
import EditorHelpModal from './modals/EditorHelpModal'; // Imported the new Modal
import FeaturedCardReview from './FeaturedCardReview';

export default function DesktopClassicLayout({
  cardParams, updateParam, updateNestedParam, overwriteAllParams,
  onOpenModal, onReset, actualFontSize, onTextRendered, onCreate, canvasRef,
  onExport, onImport, showToast,
  featuredCardsEndpoint, cardVoteEndpoint
}) {
  const { user } = useUser();

  const [hasEnteredEditor, setHasEnteredEditor] = useState(false);
  const [hasFeaturedReview, setHasFeaturedReview] = useState(false);

  const enterEditor = useCallback(() => {
    setHasEnteredEditor(true);
  }, []);

  const isFeaturedReviewVisible = !hasEnteredEditor && hasFeaturedReview;

  const [zoomLevel, setZoomLevel] = useState(100);
  const [zoomConstraints, setZoomConstraints] = useState({ min: 10, max: 300 });
  const [isArtLocked, setIsArtLocked] = useState(false);

  const [hoveredType, setHoveredType] = useState(null);
  
  // New States for requested features
  const [showDualClass, setShowDualClass] = useState(false);
  const [artInputMode, setArtInputMode] = useState('file'); // 'file' or 'url'
  const [artUrlInput, setArtUrlInput] = useState('');
  const [isArtLoading, setIsArtLoading] = useState(false);
  const [isCopyingCardImage, setIsCopyingCardImage] = useState(false);
  const [showArtAdjustPanel, setShowArtAdjustPanel] = useState(false);
  
  // State for Help Modal
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Drag and Drop States and Refs
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // States to track exact layout text sizes
  const [localActualFontSize, setLocalActualFontSize] = useState(40);
  const [templateDefaultFontSize, setTemplateDefaultFontSize] = useState(40);

  // Custom Emblem Dropdown States
  const [showEmblemDropdown, setShowEmblemDropdown] = useState(false);
  const [emblemSearchText, setEmblemSearchText] = useState('');
  const emblemDropdownRef = useRef(null);
  
  // Ref for Art Adjust container (used for click-away behavior)
  const artAdjustRef = useRef(null);
  
  // Refs for handling hidden JSON file imports and the card text area
  const jsonInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Cost Menu
const [showCostMenu, setShowCostMenu] = useState(false);
const [costMenuReady, setCostMenuReady] = useState(false);

const [costMenuPosition, setCostMenuPosition] = useState({
  top: 12,
  left: 12
});

const costButtonRef = useRef(null);
const costMenuRef = useRef(null);

const positionCostMenu = useCallback(() => {
  const button = costButtonRef.current;
  const menu = costMenuRef.current;

  if (!button || !menu) return;

  const buttonRect = button.getBoundingClientRect();
  const viewportPadding = 12;
  const horizontalGap = 8;

  const menuWidth = menu.offsetWidth;
  const menuHeight = menu.offsetHeight;

  /*
   * Open beside the button.
   * Prefer the right side, then use the left if necessary.
   */
  let left = buttonRect.right + horizontalGap;

  if (left + menuWidth > window.innerWidth - viewportPadding) {
    left = buttonRect.left - menuWidth - horizontalGap;
  }

  left = Math.max(
    viewportPadding,
    Math.min(
      left,
      window.innerWidth - menuWidth - viewportPadding
    )
  );

  /*
   * Align the vertical center of the menu with the
   * vertical center of the cog button.
   */
  const buttonCenterY =
    buttonRect.top + buttonRect.height / 2;

  let top = buttonCenterY - menuHeight / 2;

  /*
   * Keep the complete menu inside the viewport.
   */
  top = Math.max(
    viewportPadding,
    Math.min(
      top,
      window.innerHeight - menuHeight - viewportPadding
    )
  );

  setCostMenuPosition({ top, left });
}, []);

const toggleCostMenu = useCallback(() => {
  if (showCostMenu) {
    setShowCostMenu(false);
    return;
  }

  /*
   * Render invisibly first so useLayoutEffect can measure
   * and position the menu before it becomes visible.
   */
  setCostMenuReady(false);
  setShowCostMenu(true);
}, [showCostMenu]);

const selectCostType = useCallback((type) => {
  updateNestedParam('Cost', 'Type', type);

  if (
    type === 'BGStars' &&
    (cardParams.Cost.Value === null ||
      cardParams.Cost.Value === undefined ||
      cardParams.Cost.Value === '')
  ) {
    updateNestedParam('Cost', 'Value', 1);
  }
}, [cardParams.Cost.Value, updateNestedParam]);

/*
 * Measure and position before the browser paints.
 * This prevents the menu from visibly jumping when opened.
 */
useLayoutEffect(() => {
  if (!showCostMenu) return;

  positionCostMenu();
  setCostMenuReady(true);
}, [
  showCostMenu,
  cardParams.Cost.Type,
  positionCostMenu
]);

/*
 * Close on outside click or Escape.
 * Reposition if the viewport changes.
 */
useEffect(() => {
  if (!showCostMenu) return undefined;

  const handlePointerDown = (event) => {
    const clickedButton =
      costButtonRef.current?.contains(event.target);

    const clickedMenu =
      costMenuRef.current?.contains(event.target);

    if (!clickedButton && !clickedMenu) {
      setShowCostMenu(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setShowCostMenu(false);
      costButtonRef.current?.focus();
    }
  };

  const handleViewportChange = () => {
    positionCostMenu();
  };

  document.addEventListener('pointerdown', handlePointerDown);
  document.addEventListener('keydown', handleKeyDown);
  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('scroll', handleViewportChange, true);

  return () => {
    document.removeEventListener(
      'pointerdown',
      handlePointerDown
    );

    document.removeEventListener(
      'keydown',
      handleKeyDown
    );

    window.removeEventListener(
      'resize',
      handleViewportChange
    );

    window.removeEventListener(
      'scroll',
      handleViewportChange,
      true
    );
  };
}, [
  showCostMenu,
  positionCostMenu
]);

  const isFontSizeModified = cardParams.Overwrite.FontSize !== 0 && 
                             cardParams.Overwrite.FontSize !== undefined && 
                             cardParams.Overwrite.FontSize !== null;

  const formatCardCount = (count) => {
    if (count === null || count === undefined) return '—';
    const numericCount = Number(count);
    if (Number.isNaN(numericCount)) return '—';
    if (Math.abs(numericCount) >= 1000000) {
      return `${(numericCount / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    }
    if (Math.abs(numericCount) >= 1000) {
      return `${(numericCount / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return numericCount.toString();
  };

  // Close Custom Emblem Dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emblemDropdownRef.current && !emblemDropdownRef.current.contains(event.target)) {
        setShowEmblemDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close Art Adjust Panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (artAdjustRef.current && !artAdjustRef.current.contains(event.target)) {
        setShowArtAdjustPanel(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle dropped files logic
  const handleDroppedFiles = useCallback((files) => {
    enterEditor();

    const file = files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const fileType = file.type;

    // Check for JSON or .hcard configuration import
    if (fileName.endsWith('.json') || fileName.endsWith('.hcards') || fileType === 'application/json') {
      if (onImport) {
        // Construct a synthetic change event to safely pass to the existing import handler
        const mockEvent = {
          target: {
            files: [file]
          }
        };
        onImport(mockEvent);
      }
    } 
    // Check for standard image drop
    else if (fileType.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      updateParam('Art', { 
        ...cardParams.Art, 
        File: url, 
        originalUrl: '', 
        x: null, 
        y: null, 
        scale: null, 
        disableDropShadow: null   
      });
      if (showToast) {
        showToast("Loaded dropped image as card art.");
      }
    } else {
      if (showToast) {
        showToast("File type not supported. Please drop an image or a valid .json/.hcard file.");
      }
    }
  }, [onImport, updateParam, cardParams.Art, showToast, enterEditor]);

  // Window-level drag event listeners
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleDroppedFiles(e.dataTransfer.files);
      }
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleDroppedFiles]);

  // Helper for finding current Emblem info
  const selectedEmblemObj = useMemo(() => {
    for (const group of EMBLEMS) {
      const found = group.options.find(opt => opt.id === cardParams.Emblem);
      if (found) return found;
    }
    return { id: cardParams.Emblem || '', label: cardParams.Emblem || 'None' };
  }, [cardParams.Emblem]);

  // Extract the "Recent" category group from our static constants
  const recentGroup = useMemo(() => {
    return EMBLEMS.find(g => g.label === "Recent");
  }, []);

  // Filter out the "Recent" group for the default vertical list display
  const nonRecentGroups = useMemo(() => {
    return EMBLEMS.filter(g => g.label !== "Recent");
  }, []);

  const handleEmblemSelect = useCallback((emblemId) => {
    updateParam('Emblem', emblemId);
    setShowEmblemDropdown(false);
    setEmblemSearchText('');
  }, [updateParam]);

  const handleArtTransform = useCallback((transformData) => {
    updateParam('Art', { ...cardParams.Art, ...transformData });
  }, [cardParams.Art, updateParam]);

  const handleZoomSetup = useCallback(({ min, current }) => {
    setZoomConstraints({ min: Math.floor(min), max: Math.ceil(min * 3) });
    setZoomLevel(current);
  }, []);

  // Sync rendering results back into local layout states
  const handleTextRendered = useCallback((activeSize, defaultSize) => {
    setLocalActualFontSize(activeSize);
    if (defaultSize) setTemplateDefaultFontSize(defaultSize);
    if (onTextRendered) {
      onTextRendered(activeSize, defaultSize);
    }
  }, [onTextRendered]);

  // Decrements the font size cleanly from the current rendered point size
  const handleFontSizeDecrease = useCallback(() => {
    const currentSize = Math.round(actualFontSize || localActualFontSize || (templateDefaultFontSize + (cardParams.Overwrite.FontSize || 0)));
    const targetSize = currentSize - 1;
    const newOffset = targetSize - templateDefaultFontSize;
    updateNestedParam('Overwrite', 'FontSize', newOffset);
  }, [actualFontSize, localActualFontSize, templateDefaultFontSize, cardParams.Overwrite.FontSize, updateNestedParam]);

  // Increments the font size cleanly from the current rendered point size
  const handleFontSizeIncrease = useCallback(() => {
    const currentSize = Math.round(actualFontSize || localActualFontSize || (templateDefaultFontSize + (cardParams.Overwrite.FontSize || 0)));
    if (currentSize >= 40) return;
    const targetSize = currentSize + 1;
    const newOffset = targetSize - templateDefaultFontSize;
    updateNestedParam('Overwrite', 'FontSize', newOffset);
  }, [actualFontSize, localActualFontSize, templateDefaultFontSize, cardParams.Overwrite.FontSize, updateNestedParam]);

  // Wraps selected text in the textarea with tags or inserts them at cursor position
  const wrapSelectedText = useCallback((tagOpen, tagClose) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = cardParams.Text || '';

    const selectedText = text.substring(start, end);
    const before = text.substring(0, start);
    const after = text.substring(end);

    const newText = before + tagOpen + selectedText + tagClose + after;
    updateParam('Text', newText);

    // Reposition cursor inside or after tags on next render frame
    setTimeout(() => {
      textarea.focus();
      let newPos;
      if (start === end) {
        // If nothing was selected, place cursor inside the tags: [b]|[/b]
        newPos = start + tagOpen.length;
      } else {
        // If text was selected, place cursor after the tags: [b]text[/b]|
        newPos = start + tagOpen.length + selectedText.length + tagClose.length;
      }
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  }, [cardParams.Text, updateParam]);

  const handleUrlLoad = async () => {
    if (!artUrlInput) return;
    setIsArtLoading(true);
    try {
      const proxyUrl = `/api/image-proxy.php?url=${encodeURIComponent(artUrlInput)}`;
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        throw new Error("Network response was not ok. The image might be blocked or missing.");
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.startsWith('image/')) {
        throw new Error("URL does not point to a valid image format. Make sure it's a direct image link.");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      await new Promise((resolve, reject) => {
        const tempImg = new Image();
        tempImg.onload = resolve;
        tempImg.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("File loaded but is corrupted or not a valid image format."));
        };
        tempImg.src = objectUrl;
      });
      
      updateParam('Art', { 
        ...cardParams.Art, 
        File: objectUrl, 
        originalUrl: artUrlInput, 
        x: null, 
        y: null, 
        scale: null, 
        disableDropShadow: null  
      });
    } catch (err) {
      showToast(err.message || "Failed to load image. Make sure the URL is a direct image link.");
    } finally {
      setIsArtLoading(false);
    }
  };

  const handleCopyCardImage = useCallback(async () => {
    if (isCopyingCardImage) return;

    const cardCanvas = canvasRef.current;

    if (!cardCanvas?.copyToClipboard) {
      showToast?.('Card preview is not ready yet.');
      return;
    }

    const minimumVisibleTime = 10;
    const startedAt = performance.now();

    // Commit the busy UI before the canvas copy work begins.
    flushSync(() => {
      setIsCopyingCardImage(true);
    });

    try {
      await Promise.resolve(cardCanvas.copyToClipboard());
    } catch (error) {
      showToast?.(
        error?.message ||
        'Could not copy the card image. Keep this tab open and try again.'
      );
    } finally {
      // Prevent a successful fast copy from looking like a visual flicker.
      const remainingTime = minimumVisibleTime - (performance.now() - startedAt);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      setIsCopyingCardImage(false);
    }
  }, [canvasRef, isCopyingCardImage, showToast]);

  const mainType = Object.keys(SUB_TYPES).find(mainKey => 
    SUB_TYPES[mainKey].some(sub => sub.id === cardParams.Type)
  ) || 'minion';

  const getLegacyClassImg = (cls) => {
    if(cls === 'DeathKnight') return 'dk_kotft-64.png';
    if(cls === 'DemonHunter') return 'dhblizz-64.png';
    if(cls === 'None') return 'none-64.png';
    return `${cls.toLowerCase()}-64.png`;
  };

  const getSubtypePreview = (subId) => {
    const mapping = {
      'minion': 'https://www.hearthcards.net/images/typePreview/minion.webp',
      'minion_golden': 'https://www.hearthcards.net/images/typePreview/minion_golden.webp',
      'minion_diamond': 'https://www.hearthcards.net/images/typePreview/minion_diamond.webp',
      'signature26': 'https://www.hearthcards.net/images/typePreview/signature26.webp',
      'signature25': 'https://www.hearthcards.net/images/typePreview/signature25.webp',
      'spell': 'https://www.hearthcards.net/images/typePreview/spell.webp',
      'spell_golden': 'https://www.hearthcards.net/images/typePreview/spell_golden.webp',
      'anomaly': 'https://www.hearthcards.net/images/typePreview/anomaly.webp',
      'bgspell': 'https://www.hearthcards.net/images/typePreview/bgspell.webp',
      'weapon': 'https://www.hearthcards.net/images/typePreview/weapon.webp',
      'weapon_golden': 'https://www.hearthcards.net/images/typePreview/weapon_golden.webp',
      'weapon_signature': 'https://www.hearthcards.net/images/typePreview/weapon_signature.webp',
      'heropower': 'https://www.hearthcards.net/images/typePreview/heropower.webp',
      'trinket': 'https://www.hearthcards.net/images/typePreview/trinket.webp',
      'herocard': 'https://www.hearthcards.net/images/typePreview/herocard.webp',
      'herocard_golden': 'https://www.hearthcards.net/images/typePreview/herocard_golden.webp',
      'bosscard': 'https://www.hearthcards.net/images/typePreview/bosscard.webp',
      'hero': 'https://www.hearthcards.net/images/typePreview/hero.webp',
      'location': 'https://www.hearthcards.net/images/typePreview/location.webp',
      'location_golden': 'https://www.hearthcards.net/images/typePreview/location_golden.webp'
    };
    
    return mapping[subId] || 'https://www.hearthcards.net/images/typePreview/minion.webp';
  };

  const hoveredIndex = MAIN_TYPES.findIndex(type => type.id === hoveredType);
  const flyoutTop = hoveredIndex !== -1 ? Math.max(10, 40 + hoveredIndex * 76.5 - 30) : 25;

  return (
<div
  className="d-flex align-items-center justify-content-center w-100"
  style={{
    minHeight: 'calc(100vh - 75px)',
    minWidth: '1120px',
  }}
>
      <div id="desktop-classic-wrapper">
         <div 
           style={{
             position: 'fixed',
             top: 0,
             left: 0,
             width: '100vw',
             height: '100vh',
             backgroundColor: '#000000',
             backgroundImage: 'url(https://www.hearthcards.net/images/be_bg.webp)',
             backgroundAttachment: 'fixed',
             backgroundPosition: 'center center',
             backgroundRepeat: 'no-repeat',
             backgroundSize: 'cover',
             zIndex: -1,
             pointerEvents: 'none'
           }}
         />

         {isCopyingCardImage && (
           <div
             className="dcl-clipboard-copy-overlay"
             role="status"
             aria-live="polite"
             aria-label="Copying card image to clipboard"
           >
             <div className="dcl-clipboard-copy-status">
               <span
                 className="dcl-clipboard-copy-spinner"
                 aria-hidden="true"
               />

               <div>
                 <div className="dcl-clipboard-copy-title">
                   Copying card image…
                 </div>
                 <div className="dcl-clipboard-copy-message">
                   Keep this tab open for a moment.
                 </div>
               </div>
             </div>
           </div>
         )}

         {/* Drag and Drop Dashed Drop Zone Overlay */}
         {isDragging && (
           <div 
             style={{
               position: 'fixed',
               top: 0,
               left: 0,
               width: '100vw',
               height: '100vh',
               backgroundColor: 'rgba(0, 0, 0, 0.75)',
               zIndex: 99999,
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               pointerEvents: 'none'
             }}
           >
             <div 
               style={{
                 width: '90%',
                 height: '90%',
                 border: '4px dashed #ffffff',
                 borderRadius: '12px',
                 backgroundColor: 'rgba(25, 20, 15, 0.9)',
                 display: 'flex',
                 flexDirection: 'column',
                 alignItems: 'center',
                 justifyContent: 'center',
                 color: '#e0d3b8',
                 fontFamily: 'Belwe, sans-serif',
                 gap: '15px',
                 boxShadow: '0 0 20px rgba(0,0,0,0.7)'
               }}
             >
               <FaFileImport style={{ fontSize: '64px', color: '#ffb01f' }} />
               <div style={{ fontSize: '28px', fontWeight: 'bold' }}>Drop File to Load</div>
               <div style={{ fontSize: '16px', color: '#a19580', maxWidth: '450px', textAlign: 'center', lineHeight: '1.4' }}>
                 Drop an image file to import card art, or drop a .json/.hcards file to restore card configuration data.
               </div>
             </div>
           </div>
         )}

       <div 
         id="container" 
         style={{ 
           position: 'relative',
           width: 1120, 
           height: 743, 
           margin: '-40px 0 0 0',
           backgroundImage: 'url(https://www.hearthcards.net/images/panel_main_newest9.webp)' 
         }}
       >
    <a 
      href="https://www.patreon.com/hearthcards" 
      target="_blank" 
      rel="noopener noreferrer"
    >
      <div 
        style={{
          background: "url('../images/unad_en.png') no-repeat",
          minHeight: "600px",
          backgroundPositionX: "center",
          position: "absolute",
          left: "50%",
          top: "50%",
          margin: "-280px 0 0 575px",

          border: "1px solid #DDDDDD",
          backgroundColor: "#1a1816",
          borderRadius: "6px",
          padding: "5px",
          textAlign: "center"
        }}
      >
        <img src="/images/suop8.webp" alt="Patreon Campaign" />
      </div>  
    </a>

        {/* Documentation Toggle Button (Placed directly to the left of the counter) */}
        {/* <span

          title="Open Editor Documentation"
          onClick={() => setShowHelpModal(true)}
          style={{
            position: 'absolute',
            top: 85,
            left: 113,
            color: '#ffb01f',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <FaCircleQuestion className="me-2" style={{ fontSize: 14 }} /> Help
        </span> */}

        <div
          id="counter"
          style={{
            position: 'absolute',
            top: 84,
            left: 536,
            fontSize: 17,
            fontFamily: 'Belwe',
            color: 'white',
            width: 100,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 4,
            cursor: 'default',
            WebkitUserSelect: 'none',
            userSelect: 'none'
          }}
        >{formatCardCount(user?.HCCardCount)} <img src="/images/cards-icon.webp" alt="" style={{ width: 18, height: 'auto', display: 'inline-block' }} /></div>
        
             {/* CLASSIC TYPE MENU WITH HOVER SUBTYPE SQUARES */}
             <span data-editor-control style={{position: 'absolute', top: 85, left: 23, fontSize: 12, cursor: 'pointer', color: '#4A7AFF', textDecoration: 'underline'}} onClick={() => { enterEditor(); setShowCostMenu(false); onOpenModal('customCardType'); }}>Custom</span>
             <div 
               id="type_menu_container" 
               style={{ position: 'absolute', top: 110, left: 0, width: 320, height: 576, zIndex: 1000, pointerEvents: 'none' }}
               onMouseLeave={() => setHoveredType(null)}
               onPointerDownCapture={enterEditor}
               onFocusCapture={enterEditor}
             >
               <div id="type_menu" style={{ position: 'absolute', top: 0, left: 0, width: 78, height: 576, pointerEvents: 'auto' }}>
                 {MAIN_TYPES.map((type, i) => {
                   const isSelected = mainType === type.id;
                   return (
                      <div 
                        key={type.id} 
                        style={{
                          width: 51, height: 56, position: 'absolute', left: 17, top: 40 + i * 76.5, cursor: 'pointer',
                          backgroundImage: isSelected ? `url(/images/box-closed.png)` : `url()`,
                          backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center'
                        }} 
                        onClick={() => {
                          if (SUB_TYPES[type.id] && SUB_TYPES[type.id].length > 0) {
                            updateParam('Type', SUB_TYPES[type.id][0].id);
                          }
                        }}
                        onMouseEnter={() => setHoveredType(type.id)}
                        title={type.label}
                      />
                   );
                 })}
               </div>

               {/* DYNAMIC SUBTYPE FLYOUT PANEL */}
               {hoveredType && SUB_TYPES[hoveredType] && (
                 <div style={{ position: 'absolute', left: 78, top: flyoutTop, backgroundColor: 'rgba(25, 20, 15, 0.98)', border: '2px solid #5c442d', borderRadius: 8, padding: '12px', display: 'grid', gridTemplateColumns: 'repeat(2, 90px)', gap: '10px', boxShadow: '0px 4px 15px rgba(0,0,0,0.85)', pointerEvents: 'auto' }}>
                   {SUB_TYPES[hoveredType].map((sub) => {
                     const previewUrl = getSubtypePreview(sub.id);
                     const isSelected = cardParams.Type === sub.id;
                     return (
                       <div
                         key={sub.id}
                         onClick={() => {
                           updateParam('Type', sub.id);
                           setHoveredType(null);
                         }}
                         style={{ width: 90, height: 120, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', border: isSelected ? '2px solid #ffb01f' : '1px solid #4a3c31', borderRadius: 4, padding: '6px', backgroundColor: isSelected ? 'rgba(255,176,31,0.15)' : 'rgba(0,0,0,0.5)', transition: 'all 0.15s ease-in-out' }}
                         title={sub.label}
                       >
                         <div style={{ width: '100%', height: 78, backgroundImage: `url(${previewUrl})`, backgroundSize: 'contain', backgroundPosition: 'center top', backgroundRepeat: 'no-repeat' }} />
                         <div style={{ fontSize: '11px', color: isSelected ? '#ffb01f' : '#e0d3b8', textAlign: 'center', lineHeight: '1.2', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '4px', fontWeight: isSelected ? 'bold' : 'normal' }}>
                           {sub.label}
                         </div>
                       </div>
                     );
                   })}
                 </div>
               )}
             </div>

             {/* PANEL LEFT */}
             <div id="panel_left" className='user-select-none' onPointerDownCapture={enterEditor} onFocusCapture={enterEditor} style={{ backgroundImage: 'url(https://www.hearthcards.net/images/panel_main_new7_seperators.webp)', width: 481, height: 455, position: 'absolute', top: 170, left: 140, color: 'black' }}>
                 
                 {/* NAME & TRIBE */}
                 <div id="panel_left_cardname_race" style={{ width: 480, height: 62, position: "absolute", top: 84, left: 0, fontSize: 17 }}>
                   <span style={{ position: "absolute", top: 4, left: 12 }}>
                     Enter Card Name and (optionally) Type
                   </span>

                   <div style={{ position: "absolute", top: 37, left: 3, display: "flex", alignItems: "center" }}>
                     <input
                       className="legacy-input-text cardname"
                       placeholder="Card Name"
                       maxLength="30"
                       value={cardParams.Title}
                       onChange={e => updateParam("Title", e.target.value)}
                       style={{ width: 265, marginLeft: 10 }}
                     />

                       <button 
                         type="button"
                         title="Import Card"
                         className="action-btn-under"
                         onClick={() => onOpenModal("import")}
                         style={{ width: 28, height: 28 }}
                       >
                         <RiImportFill />
                       </button>

                     <input
                       className="legacy-input-text cardrace"
                       placeholder="Type, 2Type"
                       maxLength="20"
                       value={cardParams.Tribe}
                       onChange={e => updateParam("Tribe", e.target.value)}
                       style={{ width: 123, marginLeft: 15 }}
                     />

                       <button 
                         type="button"
                         title="Select Tribe"
                         className="action-btn-under"
                         onClick={() => onOpenModal("tribe")}
                         style={{ width: 28, height: 28 }}
                       >
                         <FaMagnifyingGlass />
                       </button>

                   </div>
                 </div>

                 {/* CLASSES & DUAL CLASS TOGGLE */}
                 <div id="panel_left_classes" style={{ width: 480, height: 72, position: 'absolute', top: 0, left: 0, fontSize: 17 }}>
                   <span style={{ position: 'absolute', top: 0, left: 12 }}>Choose a {showDualClass ? 'Secondary' : 'Class'}</span>
                   
                   <div style={{ position: 'absolute', top: 0, right: 8, fontSize: 17, fontWeight: 'bold' }}>
                      <span 
                        style={{ cursor: 'pointer', marginRight: 15, color: '#444' }} 
                        onClick={() => onOpenModal(showDualClass ? 'customDualClass' : 'customClass')}
                        title="Browse Custom Classes"
                      >
                        <i className="fa fa-search me-1"></i> {showDualClass ? 'Custom' : 'Custom'}
                      </span>
                      <span 
                        style={{ cursor: 'pointer', textDecoration: 'underline', color: showDualClass ? '#4A7AFF' : '#000' }} 
                        onClick={() => setShowDualClass(!showDualClass)}
                      >
                        Dual
                      </span>
                   </div>

                   <div style={{ position: 'absolute', top: 32, left: 11, display: 'flex', gap: 1 }}>
                      {/* If Dual Class is active, show the 'None' icon */}
                      {showDualClass && (
                        <React.Fragment>
                          <input type="radio" id="legacy-class-none" checked={!cardParams.DualClass || cardParams.DualClass === 'None'} onChange={() => updateParam('DualClass', '')} style={{ display: 'none' }} />
                          <label htmlFor="legacy-class-none" style={{ cursor: 'pointer', margin: 0 }}>
                            <span style={{ display: 'inline-block', width: 38, height: 38, borderRadius: 5, backgroundColor: (!cardParams.DualClass || cardParams.DualClass === 'None') ? '#4A7AFF' : 'transparent', transition: 'background-color 0.2s' }}>
                              <img className="unselectable" style={{ width: 38, height: 38 }} src={`https://www.hearthcards.net/images/classes-main/none-64.png`} alt="None" />
                            </span>
                          </label>
                        </React.Fragment>
                      )}

                      {CLASSES.filter(cls => !showDualClass || cls !== 'Neutral').map(cls => {
                        const isChecked = showDualClass ? cardParams.DualClass === cls : cardParams.Class === cls;
                        return (
                          <React.Fragment key={cls}>
                            <input type="radio" id={`legacy-class-${cls}`} name="legacy-cardclass" value={cls} checked={isChecked} onChange={() => updateParam(showDualClass ? 'DualClass' : 'Class', cls)} style={{ display: 'none' }} />
                            <label htmlFor={`legacy-class-${cls}`} style={{ cursor: 'pointer', margin: 0 }}>
                              <span style={{ display: 'inline-block', width: 38, height: 38, borderRadius: 5, backgroundColor: isChecked ? '#4A7AFF' : 'transparent', transition: 'background-color 0.2s' }}>
                                <img className="unselectable" style={{ width: 38, height: 38 }} src={`https://www.hearthcards.net/images/classes-main/${getLegacyClassImg(cls)}`} alt={cls} />
                              </span>
                            </label>
                          </React.Fragment>
                        )
                      })}
                   </div>
                 </div>



{/* MANA / STATS */}
<div
  id="panel_left_mana"
  style={{
    width: 320,
    height: 90,
    position: 'absolute',
    top: 165,
    left: 0,
    fontSize: 17
  }}
>
  <span
    style={{
      position: 'absolute',
      top: 10,
      left: 12
    }}
  >
    Cost, Attack, Health
  </span>

  <div
    style={{
      position: 'absolute',
      left: 0,
      top: 13
    }}
  >
    {/* The single cost-settings button */}
    <button
      ref={costButtonRef}
      type="button"
      onClick={toggleCostMenu}
      aria-expanded={showCostMenu}
      aria-controls="cost-settings-menu"
      aria-label="Open card cost settings"
      title="Card cost settings"
      style={{
        position: 'absolute',
        top: 30,
        left: 10,
        width: 30,
        height: 30,
        padding: 0,
        border: 'none',
        borderRadius: 6,
        background: showCostMenu
          ? 'rgba(74, 122, 255, 0.22)'
          : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
      }}
    >
      <img
        src="https://www.hearthcards.net/images/gear-cog-icon.png"
        alt=""
        style={{ width: 20 }}
      />

      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: -8,
          top: '50%',
          marginTop: -3,
          border: 'solid 5px transparent',
          borderTop: 'solid 5px #000'
        }}
      />
    </button>

    {showCostMenu && (
<div
  ref={costMenuRef}
  id="cost-settings-menu"
  role="dialog"
  aria-label="Card cost settings"
  style={{
    position: 'fixed',
    top: costMenuPosition.top,
    left: costMenuPosition.left,
    visibility: costMenuReady ? 'visible' : 'hidden',
    zIndex: 2147483647,
    width: 'min(350px, calc(100vw - 24px))',
    maxHeight: 'calc(100vh - 24px)',
    overflowY: 'auto',
    boxSizing: 'border-box',
    padding: 10,
    border: '1px solid #c9beaa',
    borderRadius: 9,
    background: '#f8f4e9',
    color: '#211a14',
    boxShadow: '0 10px 28px rgba(0, 0, 0, 0.42)',
    fontSize: 12
  }}
>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            paddingBottom: 7,
            marginBottom: 8,
            borderBottom: '1px solid #d9cfbb'
          }}
        >
          <span
            style={{
              color: '#4a3c31',
              fontSize: 14,
              fontWeight: 700
            }}
          >
            Cost Settings
          </span>

          <button
            type="button"
            onClick={() => setShowCostMenu(false)}
            aria-label="Close cost settings"
            style={{
              width: 24,
              height: 24,
              padding: 0,
              border: '1px solid #c9beaa',
              borderRadius: 5,
              background: '#fff',
              color: '#4a3c31',
              fontSize: 17,
              lineHeight: 1,
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        {/* Cost types */}
        <section style={{ marginBottom: 9 }}>
          <div
            style={{
              marginBottom: 6,
              color: '#4a3c31',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase'
            }}
          >
            Cost Type
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              gap: 5
            }}
          >
            {[
              {
                value: 'Mana',
                label: 'Mana',
                image:
                  '/assets/Card_Inhand_BannerAtlas_Minion_ManaGem.webp'
              },
              {
                value: 'Coin',
                label: 'Coin',
                image: '/images/cost_selector_coin.png'
              },
              {
                value: 'BGStars',
                label: 'BG',
                image: '/images/cost_selector_bgstars.png'
              },
              {
                value: 'ArmorCost',
                label: 'Armor',
                image: '/assets/heroshieldbannertexture.webp'
              },
              {
                value: 'HealthCost',
                label: 'Health',
                image: '/assets/healthtexture.webp'
              }
            ].map((costType) => {
              const isSelected =
                cardParams.Cost.Type === costType.value;

              return (
                <button
                  key={costType.value}
                  type="button"
                  onClick={() => selectCostType(costType.value)}
                  aria-pressed={isSelected}
                  style={{
                    minWidth: 0,
                    minHeight: 55,
                    padding: '4px 2px',
                    border: isSelected
                      ? '2px solid #4A7AFF'
                      : '1px solid #c9beaa',
                    borderRadius: 7,
                    background: isSelected ? '#e8efff' : '#fff',
                    color: '#30251d',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2
                  }}
                >
                  <img
                    src={costType.image}
                    alt=""
                    style={{
                      width: 24,
                      height: 24,
                      objectFit: 'contain'
                    }}
                  />

                  <span
                    style={{
                      overflow: 'hidden',
                      width: '100%',
                      fontSize: 10,
                      fontWeight: 700,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {costType.label}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Chronum cost */}
        {cardParams.Cost.Type === 'BGStars' && (
          <section
            style={{
              marginBottom: 9,
              paddingTop: 8,
              borderTop: '1px solid #d9cfbb'
            }}
          >
            <div
              style={{
                marginBottom: 6,
                color: '#4a3c31',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase'
              }}
            >
              Chronum Cost
            </div>

            <div
              style={{
                display: 'flex',
                gap: 5
              }}
            >
              {[
                { value: '', label: 'None' },
                { value: 1, label: '1' },
                { value: 2, label: '2' },
                { value: 3, label: '3' }
              ].map((item) => {
                const isSelected =
                  (cardParams.ChronumCost || '') === item.value;

                return (
                  <button
                    key={item.value || 'none'}
                    type="button"
                    onClick={() =>
                      updateParam('ChronumCost', item.value)
                    }
                    aria-pressed={isSelected}
                    style={{
                      minWidth: item.value === '' ? 58 : 34,
                      height: 30,
                      padding: '0 7px',
                      border: isSelected
                        ? '2px solid #4A7AFF'
                        : '1px solid #c9beaa',
                      borderRadius: 5,
                      background: isSelected ? '#4A7AFF' : '#fff',
                      color: isSelected ? '#fff' : '#30251d',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

{/* Trinket Upper/Lesser */}
{cardParams.Type === 'trinket' && (
  <section
    style={{
      marginBottom: 9,
      paddingTop: 8,
      borderTop: '1px solid #d9cfbb'
    }}
  >
    <div
      style={{
        marginBottom: 6,
        color: '#4a3c31',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase'
      }}
    >
      Upper/Lesser Trinket
    </div>

    <div
      style={{
        display: 'flex',
        gap: 5
      }}
    >
      {[
        {
          value: '',
          label: 'None'
        },
        {
          value: 'up',
          label: 'Upper',
          image: 'https://www.hearthcards.net/assets/trinket_up.webp'
        },
        {
          value: 'down',
          label: 'Lower',
          image: 'https://www.hearthcards.net/assets/trinket_down.webp'
        }
      ].map((item) => {
        const isSelected =
          (cardParams.TrinketUD || '') === item.value;

        return (
          <button
            key={item.value || 'none'}
            type="button"
            onClick={() =>
              updateParam('TrinketUD', item.value)
            }
            aria-pressed={isSelected}
            title={item.label}
            style={{
              minWidth: item.value === '' ? 58 : 42,
              height: 34,
              padding: item.image ? '2px 7px' : '0 7px',
              border: isSelected
                ? '2px solid #4A7AFF'
                : '1px solid #c9beaa',
              borderRadius: 5,
              background: isSelected ? '#4A7AFF' : '#fff',
              color: isSelected ? '#fff' : '#30251d',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {item.image ? (
              <img
                src={item.image}
                alt={item.label}
                style={{
                  height: 26,
                  width: 'auto',
                  display: 'block'
                }}
              />
            ) : (
              item.label
            )}
          </button>
        );
      })}
    </div>
  </section>
)}

{/* Banners */}
<section
  style={{
    marginBottom: 9,
    paddingTop: 8,
    borderTop: '1px solid #d9cfbb'
  }}
>
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      marginBottom: 6
    }}
  >
    <span
      style={{
        color: '#4a3c31',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase'
      }}
    >
      Banners
    </span>

    <button
      type="button"
      onClick={() => {
        setShowCostMenu(false);
        onOpenModal('customBanner');
      }}
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: '#365cb8',
        cursor: 'pointer',
        fontSize: 12,
        textDecoration: 'underline'
      }}
    >
      Browse Custom
    </button>
  </div>

  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
      gap: 5
    }}
  >
    {[
      'banner_forge.webp',
      'banner_tradeable.webp',
      'banner_prepare.webp',
      'banner_triclass_0-1.webp',
      'banner_triclass_0-2.webp',
      'banner_triclass_0-3.webp',
      'banner_sc_zerg.webp',
      'banner_sc_protoss.webp',
      'banner_sc_terran.webp'
    ].map((banner) => {
      const isSelected = cardParams.Banner === banner;

      return (
        <button
          key={banner}
          type="button"
          onClick={() =>
            updateParam(
              'Banner',
              isSelected ? '' : banner
            )
          }
          aria-pressed={isSelected}
          title={banner
            .replace('banner_', '')
            .replace('.webp', '')}
          style={{
            minWidth: 0,
            height: 52,
            padding: 3,
            border: isSelected
              ? '2px solid #e04b36'
              : '1px solid #c9beaa',
            borderRadius: 6,
            background: isSelected
              ? '#fff0ed'
              : '#fff',
            cursor: 'pointer'
          }}
        >
          <img
            src={`/assets/${banner}`}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </button>
      );
    })}

    <button
      type="button"
      onClick={() =>
        updateParam(
          'RightBanner',
          cardParams.RightBanner
            ? ''
            : 'right-banner-sc-us2.webp'
        )
      }
      aria-pressed={Boolean(cardParams.RightBanner)}
      title="Toggle right banner"
      style={{
        minWidth: 0,
        height: 52,
        padding: 3,
        border: cardParams.RightBanner
          ? '2px solid #e04b36'
          : '1px solid #c9beaa',
        borderRadius: 6,
        background: cardParams.RightBanner
          ? '#fff0ed'
          : '#fff',
        cursor: 'pointer'
      }}
    >
      <img
        src="/assets/right-banner-sc-us2.webp"
        alt="Right banner"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }}
      />
    </button>
  </div>
</section>

        {/* Runes */}
        <section
          style={{
            paddingTop: 8,
            borderTop: '1px solid #d9cfbb'
          }}
        >
          <div
            style={{
              marginBottom: 6,
              color: '#4a3c31',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase'
            }}
          >
            Death Knight Runes
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 5
            }}
          >
            {['Slot1', 'Slot2', 'Slot3'].map((slot, index) => (
              <label
                key={slot}
                style={{
                  display: 'flex',
                  minWidth: 0,
                  flexDirection: 'column',
                  gap: 2,
                  color: '#655849',
                  fontSize: 10
                }}
              >
                Rune {index + 1}

                <select
                  value={cardParams.Runes?.[slot] || ''}
                  onChange={(event) =>
                    updateNestedParam(
                      'Runes',
                      slot,
                      event.target.value
                    )
                  }
                  style={{
                    width: '100%',
                    minWidth: 0,
                    padding: '4px 2px',
                    border: '1px solid #bcae98',
                    borderRadius: 4,
                    background: '#fff',
                    color: '#30251d',
                    fontSize: 10
                  }}
                >
                  <option value="">None</option>
                  <option value="CardRunes_DeathKnight_Blood.webp">
                    Blood
                  </option>
                  <option value="CardRunes_DeathKnight_Frost.webp">
                    Frost
                  </option>
                  <option value="CardRunes_DeathKnight_Unholy.webp">
                    Unholy
                  </option>
                </select>
              </label>
            ))}
          </div>
        </section>
      </div>
    )}

    {/* Cost input */}
    {cardParams.Cost.Type === 'BGStars' ? (
      <select
        className="legacy-input-text cardmana"
        value={cardParams.Cost.Value ?? 1}
        onChange={(event) =>
          updateNestedParam('Cost', 'Value', event.target.value)
        }
        style={{
          marginLeft: 50,
          marginTop: 30,
          width: 50,
          textAlign: 'center',
          padding: 0
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7].map((num) => (
          <option key={num} value={num}>
            {num}
          </option>
        ))}
      </select>
    ) : (
      <input
        className="legacy-input-text cardmana"
        placeholder="C"
        maxLength="2"
        value={cardParams.Cost.Value ?? ''}
        onChange={(event) =>
          updateNestedParam('Cost', 'Value', event.target.value)
        }
        style={{
          marginLeft: 50,
          marginTop: 30,
          width: 50,
          textAlign: 'center'
        }}
      />
    )}

    <input
      className="legacy-input-text cardattack"
      placeholder="A"
      maxLength="2"
      value={cardParams.Attack}
      onChange={(event) =>
        updateParam('Attack', event.target.value)
      }
      style={{
        marginLeft: 10,
        marginTop: 30,
        width: 50,
        textAlign: 'center'
      }}
    />

    <input
      className="legacy-input-text cardhealth"
      placeholder="H"
      maxLength="3"
      value={cardParams.Health}
      onChange={(event) =>
        updateParam('Health', event.target.value)
      }
      style={{
        marginLeft: 10,
        marginTop: 30,
        width: 50,
        textAlign: 'center'
      }}
    />
  </div>
</div>

                 {/* RARITY */}
                 <div id="panel_left_rarity" style={{ width: 220, height: 90, position: 'absolute', top: 165, left: 253, fontSize: 17 }}>
                   <span style={{ position: 'absolute', top: 10, left: 12 }}>Card Rarity</span>
                   <div style={{ position: 'absolute', left: 4, top: 41, display: 'flex' }}>
                      {RARITIES.map(rarity => {
                        const isChecked = cardParams.Rarity === rarity;
                        const val = rarity.toLowerCase();
                        const img = rarity === 'Free' ? 'gem_basic.png' : `gem_${val}.png`;
                        return (
                          <React.Fragment key={rarity}>
                            <input type="radio" id={`legacy-gem-${val}`} name="legacy-gem" value={rarity} checked={isChecked} onChange={() => updateParam('Rarity', rarity)} style={{ display: 'none' }} />
                            <label htmlFor={`legacy-gem-${val}`} style={{ cursor: 'pointer', margin: 0, position: 'relative' }}>
                              <span style={{ display: 'inline-block', width: 27, height: 34, borderRadius: '100px / 130px', backgroundColor: isChecked ? '#4A7AFF' : 'transparent', position: 'absolute', left: 17, top: -3 }}></span>
                              <img className="unselectable" style={{ position: 'relative', marginLeft: 18 }} src={`https://www.hearthcards.net/images/gem/${img}`} alt={rarity} />
                            </label>
                          </React.Fragment>
                        )
                      })}
                   </div>
                   
                   <img src="https://www.hearthcards.net/images/dragon_head_switch.png" className={`unselectable ${cardParams.Overwrite.Dragon === 'show' || (cardParams.Rarity === 'Legendary' && cardParams.Overwrite.Dragon !== 'hide') ? 'legacy-show' : 'legacy-hide'}`} style={{ height: 24, position: 'absolute', top: 8, left: 151, cursor: 'pointer' }} onClick={() => {
                       const isCurrentlyActive = cardParams.Overwrite.Dragon === 'show' || (cardParams.Rarity === 'Legendary' && cardParams.Overwrite.Dragon !== 'hide');
                       updateNestedParam('Overwrite', 'Dragon', isCurrentlyActive ? 'hide' : 'show');
                   }} alt="Dragon" />
                   
                   <img src="https://www.hearthcards.net/images/emblem_switch.png" className={`unselectable ${cardParams.Overwrite.Emblem === 'show' || (cardParams.Rarity !== 'Free' && cardParams.Overwrite.Emblem !== 'hide') ? 'legacy-show' : 'legacy-hide'}`} style={{ height: 24, position: 'absolute', top: 8, left: 195, cursor: 'pointer' }} onClick={() => {
                       const isCurrentlyActive = cardParams.Overwrite.Emblem === 'show' || (cardParams.Rarity !== 'Free' && cardParams.Overwrite.Emblem !== 'hide');
                       updateNestedParam('Overwrite', 'Emblem', isCurrentlyActive ? 'hide' : 'show');
                   }} alt="Emblem" />
                 </div>

                 {/* CARD TEXT */}
                 <div id="panel_left_cardtext" style={{ width: 320, height: 100, position: 'absolute', top: 276, left: 0, fontSize: 17 }}>
                   <span style={{ position: 'absolute', top: -2, left: 262, zIndex: 3, cursor: 'pointer' }} onClick={() => wrapSelectedText('[b]', '[/b]')}>
                     <img src="https://www.hearthcards.net/images/bold-icon.png" alt="bold" />
                   </span>
                   <span style={{ position: 'absolute', top: 15, left: 262, zIndex: 3, cursor: 'pointer' }} onClick={() => wrapSelectedText('[i]', '[/i]')}>
                     <img src="https://www.hearthcards.net/images/italic-icon.png" alt="italic" />
                   </span>

                   <div style={{ position: 'absolute', top: 0, left: 13 }}>
                     <textarea ref={textareaRef} className="legacy-input-text cardtext" placeholder="Card Text" rows="4" value={cardParams.Text} onChange={e => updateParam('Text', e.target.value)} style={{ width: 270, height: 98, paddingRight: 35, resize: 'none' }}></textarea>
                   </div>
                   
                  <div style={{ position: 'absolute', left: 285, width: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                     <input 
                       type="button" 
                       value="+" 
                       style={{ width: 24, height: 24, padding: 0, cursor: 'pointer', fontSize: 12 }} 
                       onClick={handleFontSizeIncrease} 
                     />
                     
                     <span 
                       onClick={() => {
                         if (isFontSizeModified) {
                           updateNestedParam('Overwrite', 'FontSize', 0);
                         }
                       }}
                       title={isFontSizeModified ? "Click to reset to automatic font size" : undefined}
                       style={{ 
                         width: 24, 
                         textAlign: 'center', 
                         display: 'inline-block', 
                         fontSize: 14, 
                         fontWeight: 'bold',
                         color: isFontSizeModified ? '#ff0004' : '#000000',
                         cursor: isFontSizeModified ? 'pointer' : 'default',
                         transition: 'color 0.15s ease'
                       }}
                       className={isFontSizeModified ? "font-size-modified" : ""}
                     >
                       {Math.round(actualFontSize || localActualFontSize || (templateDefaultFontSize + (cardParams.Overwrite.FontSize || 0)))}
                     </span>
                     
                     <input 
                       type="button" 
                       value="-" 
                       style={{ width: 24, height: 24, padding: 0, cursor: 'pointer', fontSize: 12 }} 
                       onClick={handleFontSizeDecrease} 
                     />

                     {/* Inline relative container for the quality adjustment button and its dropdown */}
                     <div ref={artAdjustRef} style={{ position: 'relative', width: 24, height: 24 }}>
                       <button
                         type="button"
                         className="action-btn-under"
                         title="Adjust Image Quality"
                         onClick={() => setShowArtAdjustPanel(!showArtAdjustPanel)}
                         style={{ width: 24, padding: 0, height: 24, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                       >
                         <FaSliders />
                       </button>

                       {/* IMAGE ADJUSTMENTS OVERLAY */}
                       {showArtAdjustPanel && (
                         <div style={{
                           position: 'absolute',
                           bottom: '100%',
                           right: 0,
                           marginBottom: '8px',
                           width: 250,
                           backgroundColor: 'white',
                           border: '1px solid black',
                           borderRadius: 6,
                           padding: '12px',
                           boxShadow: '0px 0px 15px 1px black',
                           color: 'black',
                           zIndex: 2147483647,
                           fontSize: 13,
                           fontFamily: 'sans-serif',
                           textAlign: 'left'
                         }}>
                           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '1px solid #ccc', paddingBottom: 4 }}>
                             <span style={{ fontWeight: 'bold', fontSize: 13, fontFamily: 'Belwe', color: 'black' }}>Adjustments</span>
                             <button 
                               type="button" 
                               style={{ background: 'none', border: 'none', color: '#4A7AFF', cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}
                               onClick={() => {
                                 updateParam('Art', {
                                   ...cardParams.Art,
                                   brightness: 100,
                                   contrast: 100,
                                   saturation: 100,
                                   hue: 0,
                                   flipX: false
                                 });
                               }}
                             >
                               Reset
                             </button>
                           </div>
                           
                           <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                             {/* Brightness */}
                             <div>
                               <div style={{ display: 'flex', justifyContent: 'space-between', color: 'black' }}>
                                 <span>Brightness</span>
                                 <span>{cardParams.Art?.brightness ?? 100}%</span>
                               </div>
                               <input 
                                 type="range" 
                                 min="0" max="200" 
                                 value={cardParams.Art?.brightness ?? 100}
                                 onChange={(e) => updateParam('Art', { ...cardParams.Art, brightness: Number(e.target.value) })}
                                 style={{ width: '100%', accentColor: '#4A7AFF' }}
                               />
                             </div>

                             {/* Contrast */}
                             <div>
                               <div style={{ display: 'flex', justifyContent: 'space-between', color: 'black' }}>
                                 <span>Contrast</span>
                                 <span>{cardParams.Art?.contrast ?? 100}%</span>
                               </div>
                               <input 
                                 type="range" 
                                 min="0" max="200" 
                                 value={cardParams.Art?.contrast ?? 100}
                                 onChange={(e) => updateParam('Art', { ...cardParams.Art, contrast: Number(e.target.value) })}
                                 style={{ width: '100%', accentColor: '#4A7AFF' }}
                               />
                             </div>

                             {/* Saturation */}
                             <div>
                               <div style={{ display: 'flex', justifyContent: 'space-between', color: 'black' }}>
                                 <span>Saturation</span>
                                 <span>{cardParams.Art?.saturation ?? 100}%</span>
                               </div>
                               <input 
                                 type="range" 
                                 min="0" max="200" 
                                 value={cardParams.Art?.saturation ?? 100}
                                 onChange={(e) => updateParam('Art', { ...cardParams.Art, saturation: Number(e.target.value) })}
                                 style={{ width: '100%', accentColor: '#4A7AFF' }}
                               />
                             </div>

                             {/* Hue Shift */}
                             <div>
                               <div style={{ display: 'flex', justifyContent: 'space-between', color: 'black' }}>
                                 <span>Hue Shift</span>
                                 <span>{cardParams.Art?.hue ?? 0}°</span>
                               </div>
                               <input 
                                 type="range" 
                                 min="0" max="360" 
                                 value={cardParams.Art?.hue ?? 0}
                                 onChange={(e) => updateParam('Art', { ...cardParams.Art, hue: Number(e.target.value) })}
                                 style={{ width: '100%', accentColor: '#4A7AFF' }}
                               />
                             </div>

                             {/* Flip Horizontal */}
                             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                               <span style={{ color: 'black' }}>Flip Horizontal</span>
                               <button
                                 type="button"
                                 onClick={() => updateParam('Art', { ...cardParams.Art, flipX: !(cardParams.Art?.flipX ?? false) })}
                                 style={{
                                   padding: '4px 10px',
                                   borderRadius: 4,
                                   border: '1px solid #ccc',
                                   background: (cardParams.Art?.flipX ?? false) ? '#4A7AFF' : '#e0e0e0',
                                   color: (cardParams.Art?.flipX ?? false) ? '#fff' : '#000',
                                   cursor: 'pointer',
                                   fontSize: 11,
                                   fontWeight: 'bold'
                                 }}
                               >
                                 {(cardParams.Art?.flipX ?? false) ? 'Flipped' : 'Normal'}
                               </button>
                             </div>
                           </div>
                         </div>
                       )}
                     </div>
                  </div>

                   {/* CUSTOM EMBLEM SELECTOR */}
                   <div id="emblem_style" style={{ position: 'absolute', left: 315, top: 0, width: 158, fontSize: 16 }} ref={emblemDropdownRef}>
                     <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4, justifyContent: 'space-between' }}>
                       <span>Set Emblem</span> 

                       <button 
                         type="button"
                         title="Browse Custom Emblems"
                         className="action-btn-under"
                         onClick={() => onOpenModal("customEmblem")}
                         style={{ width: 24, height: 24 }}
                       >
                         <FaMagnifyingGlass />
                       </button>
                     </div>

                     {/* Custom Dropdown Trigger */}
                     <div 
                       onClick={() => setShowEmblemDropdown(!showEmblemDropdown)}
                       className="legacy-input-text"
                          style={{ 
                          width: '100%', height: 32, padding: '2px 6px', display: 'flex', alignItems: 'center', 
                          cursor: 'pointer', backgroundColor: '#fff', border: '1px solid #aaa', 
                          borderRadius: 4, userSelect: 'none', position: 'relative', color: '#444'
                          }}
                     >
                        {selectedEmblemObj.id && selectedEmblemObj.id !== 'None' && selectedEmblemObj.id !== '' && (
                          <img 
                            src={`https://www.hearthcards.net/images/selectorEmblems/${selectedEmblemObj.id}.png`} 
                            alt="" 
                            style={{ width: 18, height: 18, marginRight: 6, objectFit: 'contain' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                          <span style={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13, color: '#444', textAlign: 'left', fontFamily: 'Belwe', fontWeight: 'bold' }}>
                          {selectedEmblemObj.label || 'None'}
                          </span>
                        <span style={{ fontSize: 10, color: '#333' }}>▼</span>
                     </div>

                     {/* JSON Actions, Clipboard and Download Buttons under Set Emblem Selector */}
                     <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', width: '100%' }}>
                       <button 
                         type="button"
                         title="Import JSON"
                         className="action-btn-under"
                         onClick={() => jsonInputRef.current?.click()}
                       >
                         <FaFileImport />
                       </button>

                       <button 
                         type="button"
                         title="Export JSON"
                         className="action-btn-under"
                         onClick={onExport}
                       >
                         <FaFileExport />
                       </button>

                       <button 
                         type="button"
                         title={isCopyingCardImage
                           ? 'Copying Card Image…'
                           : 'Copy Card Image to Clipboard'}
                         className="action-btn-under"
                         onClick={handleCopyCardImage}
                         disabled={isCopyingCardImage}
                         aria-busy={isCopyingCardImage}
                       >
                         <FaCamera />
                       </button>

                       <button 
                         type="button"
                         title="Download Preview Image"
                         className="action-btn-under"
                         onClick={() => canvasRef.current?.exportCard()}
                       >
                         <FaDownload />
                       </button>
                     </div>

                     {/* Hidden File Input for JSON import */}
                     <input 
                       type="file" 
                       accept=".json" 
                       style={{ display: 'none' }} 
                       ref={jsonInputRef} 
                       onChange={onImport} 
                     />

                     {/* Dropdown Menu Overlay */}
                     {showEmblemDropdown && (
                       <div style={{ 
                         position: 'absolute', top: 56, right: 0, width: 'max-content', minWidth: 250, maxHeight: 330, 
                         backgroundColor: '#f0ebd8', border: '2px solid #5c442d', borderRadius: 6, 
                         zIndex: 2000, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.6)',
                         overflow: 'hidden'
                       }}>
                          {/* Static "Recent" Row Displayed as Horizontal Row of Big Images */}
                          {recentGroup && recentGroup.options.length > 0 && (
                            <div style={{ padding: '8px', borderBottom: '1px solid #c9beaa', backgroundColor: '#e5dbcc' }}>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', flexWrap: 'nowrap', overflowX: 'auto' }}>
                                {recentGroup.options.map(opt => {
                                  const isSelected = cardParams.Emblem === opt.id;
                                  return (
                                    <div 
                                      key={opt.id}
                                      title={opt.label}
                                      onClick={() => handleEmblemSelect(opt.id)}
                                      style={{ 
                                        width: 44, height: 44, cursor: 'pointer', borderRadius: 6, 
                                        backgroundColor: isSelected ? '#ffb01f' : 'rgba(255,255,255,0.5)', 
                                        border: isSelected ? '1px solid #c27d00' : '1px solid transparent',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.15s',
                                        flexShrink: 0
                                      }}
                                      onMouseEnter={(e) => { if(!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)'; }}
                                      onMouseLeave={(e) => { if(!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.5)'; }}
                                    >
                                      <img 
                                        src={`https://www.hearthcards.net/images/selectorEmblems/${opt.id}.png`} 
                                        alt={opt.label}
                                        style={{ maxWidth: 36, maxHeight: 36, objectFit: 'contain' }}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Search Field */}
                          <div style={{ padding: '6px', borderBottom: '1px solid #c9beaa' }}>
                            <input 
                              type="text"
                              placeholder="Search emblems..."
                              value={emblemSearchText}
                              onChange={e => setEmblemSearchText(e.target.value)}
                              style={{ 
                                  width: '100%', padding: '5px 8px', fontSize: 13, border: '1px solid #aaa', 
                                  borderRadius: 4, backgroundColor: '#fff', color: '#444', outline: 'none' 
                              }}
                              autoFocus
                            />
                          </div>
                          
                          {/* Options List */}
                          <div style={{ overflowY: 'auto', flexGrow: 1, paddingBottom: 4 }}>
                            {(emblemSearchText ? EMBLEMS : nonRecentGroups).map(group => {
                              const filteredOptions = group.options.filter(opt => opt.label.toLowerCase().includes(emblemSearchText.toLowerCase()));
                              if (filteredOptions.length === 0) return null;

                              return (
                                <div key={group.label}>
                                  <div style={{ 
                                    padding: '4px 8px', fontSize: 11, fontWeight: 'bold', 
                                    backgroundColor: '#e0d5c1', color: '#4a3c31', textTransform: 'uppercase' 
                                  }}>
                                    {group.label}
                                  </div>
                                  {filteredOptions.map(opt => (
                                    <div 
                                      key={opt.id}
                                      onClick={() => handleEmblemSelect(opt.id)}
                                      style={{ 
                                        padding: '6px 10px', display: 'flex', alignItems: 'center', cursor: 'pointer', 
                                        fontSize: 13, color: '#111', whiteSpace: 'nowrap', 
                                        backgroundColor: cardParams.Emblem === opt.id ? '#ffe6b3' : 'transparent',
                                        transition: 'background 0.1s'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = cardParams.Emblem === opt.id ? '#ffe6b3' : 'rgba(255,255,255,0.5)'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = cardParams.Emblem === opt.id ? '#ffe6b3' : 'transparent'}
                                    >
                                      {opt.id && opt.id !== 'None' && opt.id !== '' && (
                                        <img 
                                          src={`https://www.hearthcards.net/images/selectorEmblems/${opt.id}.png`} 
                                          alt="" 
                                          style={{ width: 22, height: 22, marginRight: 8, objectFit: 'contain' }}
                                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                      )}
                                      <span>{opt.label}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                            {/* Fallback for no search results */}
                            {EMBLEMS.every(g => g.options.filter(o => o.label.toLowerCase().includes(emblemSearchText.toLowerCase())).length === 0) && (
                              <div style={{ padding: '15px', fontSize: 13, color: '#666', textAlign: 'center' }}>No results found</div>
                            )}
                          </div>
                       </div>
                     )}
                   </div>
                 </div>

                 {/* PICTURE FILE OR URL */}
                 <div id="panel_left_picture" style={{ width: 481, height: 62, position: 'absolute', top: 398, left: 15, fontSize: 17 }}>
                   <div style={{ marginBottom: 4, textAlign: 'left' }}>
                     <span style={{ cursor: 'pointer', textDecoration: artInputMode === 'file' ? 'underline' : 'none', fontWeight: artInputMode === 'file' ? 'bold' : 'normal' }} onClick={() => setArtInputMode('file')}>Picture</span> 
                     <span> (or </span>
                     <span style={{ cursor: 'pointer', textDecoration: artInputMode === 'url' ? 'underline' : 'none', fontWeight: artInputMode === 'url' ? 'bold' : 'normal' }} onClick={() => setArtInputMode('url')}>URL</span>
                     <span>):</span>
                   </div>

                   {artInputMode === 'file' ? (
                     <input style={{ position: 'absolute', top: 24, left: 0, width: 348, fontSize: 14, fontWeight: 'normal', fontFamily: 'sans-serif' }} type="file" accept="image/*" onChange={(e) => {
                       if(e.target.files[0]) {
                         const url = URL.createObjectURL(e.target.files[0]);
                         updateParam('Art', { ...cardParams.Art, File: url, originalUrl: '', x: null, y: null, scale: null, disableDropShadow: null   });
                       }
                     }} />
                   ) : (
                     <div style={{ position: 'absolute', top: 24, left: 0, width: 348, display: 'flex', gap: 6 }}>
                        <input 
                          type="text" 
                          className="legacy-input-text" 
                          placeholder="Image URL (jpg/png/webp)"
                          value={artUrlInput}
                          onChange={(e) => setArtUrlInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleUrlLoad(); }}
                          style={{ flexGrow: 1 }}
                          disabled={isArtLoading}
                        />
                        <button 
                          type="button" 
                          style={{ cursor: isArtLoading ? 'not-allowed' : 'pointer', borderRadius: 4, border: '1px solid #ccc', background: '#e0e0e0', padding: '0 12px', fontSize: 14, fontWeight: 'bold' }}
                          onClick={handleUrlLoad}
                          disabled={isArtLoading}
                        >
                          {isArtLoading ? '...' : 'Load'}
                        </button>
                     </div>
                   )}
                   
                   <input type="button" value="Create" className="legacy-create-button" onClick={onCreate} />

                 </div>

             </div>

             {/* CARD PREVIEW */}
             <div id="card-wrapper" style={{ position: 'absolute', top: 55, left: 678, width: 430, height: 590, zIndex: 10, filter: 'drop-shadow(0px 0px 15px rgba(0,0,0,0.7))' }}>
                 <div
                   aria-hidden={isFeaturedReviewVisible}
                   style={{
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     width: '100%',
                     height: '100%',
                     overflow: 'hidden',
                     opacity: isFeaturedReviewVisible ? 0 : 1,
                     pointerEvents: isFeaturedReviewVisible ? 'none' : 'auto',
                     transition: 'opacity 180ms ease'
                   }}
                 >
                     <CardCanvas 
                       ref={canvasRef}
                       cardParams={cardParams} 
                       isArtLocked={isArtLocked} 
                       zoomLevel={zoomLevel} 
                       onZoomChange={setZoomLevel}
                       zoomMin={zoomConstraints.min}
                       zoomMax={zoomConstraints.max}
                       artUrl={cardParams.Art?.File || ''} 
                       onZoomConstraints={handleZoomSetup}
                       onArtTransform={handleArtTransform}
                       onTextRendered={handleTextRendered}
                       showToast={showToast}
                     />
                 </div>
             </div>

             <FeaturedCardReview
               enabled={!hasEnteredEditor}
               cardsEndpoint={featuredCardsEndpoint}
               voteEndpoint={cardVoteEndpoint}
               onAvailabilityChange={setHasFeaturedReview}
               onComplete={enterEditor}
               showToast={showToast}
             />

    <div
      id="bottomPanel"
      style={{
        position: "absolute",
        bottom: 6,
        left: 0,
        width: 663,
        height: 57,
        textAlign: "center",
      }}
    >
      <Link to="/gallery/"><img src="/images/gallery_button.png" alt="Gallery" className="unselectable" /></Link>
      <a href="https://www.patreon.com/hearthcards" id="patreon_button"><img src="/images/patreon_button.png" alt="Patreon" className="unselectable" /></a>
      <a href="/sets/" id="setsbutton"><img src="/images/sets_button.png" alt="Sets" className="unselectable" /></a>
    </div>

    <div style={{ position: "absolute", bottom: 17, right: 27, color: "#646464", fontSize: 14, textShadow: "0 1px 0 rgba(0,0,0,1)", textAlign: "right", }} > HearthCards 2014-2026 </div>

             {/* ZOOM SLIDER & PAN LOCK */}
             <div data-editor-control onPointerDownCapture={enterEditor} onFocusCapture={enterEditor} style={{ position: 'absolute', width: 200, left: 820, top: 663, zIndex: 15, display: isFeaturedReviewVisible ? 'none' : 'flex', alignItems: 'center', gap: 10 }}>
                <input 
                   type="range" 
                   min={zoomConstraints.min} max={zoomConstraints.max}
                   value={zoomLevel}
                   onChange={e => setZoomLevel(Number(e.target.value))}
                   onMouseUp={() => handleArtTransform({scale: zoomLevel/100})}
                   style={{ width: 135, cursor: 'pointer' }}
                   className="legacy-zoom-slider"
                />

             </div>
         </div>
      </div>

      {/* Render the Help Modal Overlay */}
      <EditorHelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
      />
    </div>
  );
}